import logger from 'shared/services/logger'
import { window } from 'shared/utils/browser'
import {
  clearCache,
  getClientSession,
} from 'shared/utils/auth/v2/client-session'
import { UserError } from 'shared/utils/errors'

/**
 * Module-level cache to prevent multiple simultaneous refresh requests.
 * Uses a static key since this is client-only (one user per browser tab).
 * Similar to the pattern used in RefreshSessionClient for gRPC.
 */
const pendingRefreshes = new Map<string, Promise<string>>()

/**
 * Custom error for tracking legacy GraphQL session logouts
 */
export class LegacySessionLogoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LegacySessionLogoutError'
  }
}

/**
 * Redirects user to logout page
 * @param path - The path to redirect to (defaults to /auth/logout)
 */
export function redirectTo(path = '/auth/logout'): void {
  if (window) {
    !window.location.pathname.startsWith(path) && window.location.replace(path)
  } else {
    logger.debug(`unable to redirect to the login page from SSR`)
  }
}

/**
 * Logs the user out and reports the event for monitoring
 * @param operationName - The GraphQL operation that triggered the logout
 * @param reason - Why the logout occurred
 */
export function logoutUser(operationName: string, reason: string): void {
  const logoutError = new LegacySessionLogoutError(`User logged out: ${reason}`)

  logger.error(logoutError, {
    key: 'graphql-session-logout',
    feature: logger.Feature.Auth,
    extra: {
      operationName,
      reason,
      timestamp: new Date().toISOString(),
      // Tag for legacy GraphQL system tracking
      isLegacyGraphQL: true,
    },
  })

  clearCache()
  redirectTo('/auth/logout')
}

/**
 * CLIENT-ONLY function - do not call on server.
 * On server, use getSession() or getPageRouterSession() instead.
 *
 * Uses a module-level cache to deduplicate concurrent refresh requests.
 *
 * @returns Promise that resolves with new access token or rejects with error
 * @throws UnauthorizedError, ForbiddenError - Auth failures (should logout)
 * @throws Error - Network or other failures after max retries (should NOT logout)
 */
export async function attemptTokenRefresh(): Promise<string> {
  // Guard against server-side execution
  if (!window) {
    throw new Error(
      'attemptTokenRefresh() is client-only. Use getSession() on server.',
    )
  }

  // Use static cache key (safe: one user per browser tab)
  const cacheKey = 'current-refresh'

  // Return existing refresh promise if one is already in progress
  const existingRefresh = pendingRefreshes.get(cacheKey)
  if (existingRefresh) {
    logger.debug('Returning existing token refresh promise')
    return existingRefresh
  }

  // Clear the session cache to force a fresh token fetch
  clearCache()

  const refreshPromise = (async () => {
    // getClientSession now has built-in retry via retrySessionFetch in makeSessionRequest
    const session = await getClientSession()

    if (!session?.accessToken) {
      throw new UserError('No session after refresh attempt', {
        rpcStatusCode: 'UNAUTHENTICATED',
        httpStatusCode: 401,
        feature: logger.Feature.Auth,
      })
    }

    logger.debug('Token refreshed successfully')
    return session.accessToken
  })()

  pendingRefreshes.set(cacheKey, refreshPromise)

  try {
    return await refreshPromise
  } finally {
    // Clear the cached promise after completion (success or failure)
    pendingRefreshes.delete(cacheKey)
  }
}
